import { useState, useEffect, useCallback } from 'react';
import { prospectsAPI, inventoryAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  Upload, Trash2, Search, X, MoreVertical, Send, ChevronLeft, ChevronRight,
  FileSpreadsheet, Bell, BellOff, AlertTriangle, Users, MailCheck, Loader2, Download
} from 'lucide-react';
import { BulkSelectCheckbox, BulkActionBar } from '@/components/BulkActions';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';

export default function Prospects() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Upload state
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // Alert state
  const [alertOpen, setAlertOpen] = useState(false);
  const [inventory, setInventory] = useState([]);
  const [selectedInventory, setSelectedInventory] = useState([]);
  const [alertMessage, setAlertMessage] = useState('');
  const [sendingAlert, setSendingAlert] = useState(false);

  // Batch state
  const [batches, setBatches] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Test email state
  const [testEmailOpen, setTestEmailOpen] = useState(false);
  const [testEmailAddr, setTestEmailAddr] = useState('');
  const [testEmailSending, setTestEmailSending] = useState(false);
  const [testEmailResult, setTestEmailResult] = useState(null);
  const [emailActivity, setEmailActivity] = useState([]);

  const toggleSelect = (id) => {
    setSelectAll(false);
    setSelected(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  };

  const handleSelectAllProspects = () => {
    setSelectAll(true);
    setSelected(prospects.map(p => p.id));
  };

  const handleClearSelection = () => {
    setSelectAll(false);
    setSelected([]);
  };

  const activeCount = selectAll ? total : selected.length;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await prospectsAPI.list({ search: searchQuery, page, limit: 50 });
      setProspects(r.data.items || []);
      setTotalPages(r.data.pages || 1);
      setTotal(r.data.total || 0);
    } catch {
      toast.error('Failed to load prospects');
    }
    setLoading(false);
  }, [searchQuery, page]);

  useEffect(() => { load(); }, [load]);

  const loadBatches = useCallback(async () => {
    try {
      const r = await prospectsAPI.batches();
      setBatches(r.data || []);
    } catch (e) { console.error('Operation failed:', e); }
  }, []);

  useEffect(() => { loadBatches(); }, [loadBatches]);

  useEffect(() => {
    inventoryAPI.list({ limit: 100 }).then(r => {
      setInventory((r.data.items || r.data || []).filter(i => i.status === 'Available'));
    }).catch(() => {});
  }, []);

  // Search debounce
  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  const handleUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    try {
      const r = await prospectsAPI.upload(uploadFile);
      toast.success(`Imported ${r.data.imported} prospects (${r.data.skipped} skipped)`);
      setUploadOpen(false);
      setUploadFile(null);
      load();
      loadBatches();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
    setUploading(false);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const r = await prospectsAPI.exportExcel();
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `CTS_Prospects_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Prospects exported successfully');
    } catch {
      toast.error('Failed to export prospects');
    }
    setExporting(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this prospect?')) return;
    try {
      await prospectsAPI.delete(id);
      toast.success('Prospect deleted');
      load();
    } catch { toast.error('Failed to delete'); }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.length} prospect(s)?`)) return;
    try {
      await prospectsAPI.bulkDelete(selected);
      toast.success(`${selected.length} prospect(s) deleted`);
      setSelected([]);
      load();
      loadBatches();
    } catch { toast.error('Failed to delete'); }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm(`DELETE ALL ${total} PROSPECTS? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      const r = await prospectsAPI.deleteAll();
      toast.success(`Deleted ${r.data.deleted} prospects`);
      setSelected([]);
      load();
      loadBatches();
    } catch { toast.error('Failed to delete all'); }
    setDeleting(false);
  };

  const handleDeleteBatch = async (batch) => {
    if (!window.confirm(`Delete ${batch.count} prospects from "${batch.file}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      const r = await prospectsAPI.deleteBatch(batch.batch_id);
      toast.success(`Deleted ${r.data.deleted} prospects from batch`);
      setSelected([]);
      load();
      loadBatches();
    } catch { toast.error('Failed to delete batch'); }
    setDeleting(false);
  };

  const toggleAlerts = async (prospect) => {
    try {
      await prospectsAPI.update(prospect.id, { alerts_enabled: !prospect.alerts_enabled });
      setProspects(prev => prev.map(p => p.id === prospect.id ? { ...p, alerts_enabled: !p.alerts_enabled } : p));
    } catch { toast.error('Failed to update'); }
  };

  const handleTestEmail = async () => {
    if (!testEmailAddr) return;
    setTestEmailSending(true);
    setTestEmailResult(null);
    try {
      const r = await prospectsAPI.testEmail(testEmailAddr);
      setTestEmailResult(r.data);
      if (r.data.success) {
        toast.success('Test email sent! Check your inbox (and spam folder).');
      } else {
        toast.error(`Failed: ${r.data.error || 'Unknown error'}`);
      }
      // Load activity after a short delay
      setTimeout(async () => {
        try {
          const a = await prospectsAPI.emailActivity();
          setEmailActivity(a.data || []);
        } catch (e) { console.error('Operation failed:', e); }
      }, 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test email failed');
      setTestEmailResult({ success: false, error: err.response?.data?.detail || 'Request failed' });
    }
    setTestEmailSending(false);
  };

  const openTestEmail = async () => {
    setTestEmailOpen(true);
    setTestEmailResult(null);
    setTestEmailAddr('');
    try {
      const a = await prospectsAPI.emailActivity();
      setEmailActivity(a.data || []);
    } catch (e) { console.error('Operation failed:', e); }
  };

  const handleBulkToggleAlerts = async (enabled) => {
    if (selected.length === 0) return;
    try {
      await prospectsAPI.toggleAlerts({ ids: selected, enabled });
      toast.success(`Alerts ${enabled ? 'enabled' : 'disabled'} for ${selected.length} prospect(s)`);
      load();
    } catch { toast.error('Failed to update alerts'); }
  };

  const openAlertDialog = () => {
    if (!selectAll && selected.length === 0) {
      toast.error('Select at least one prospect');
      return;
    }
    setAlertOpen(true);
    setSelectedInventory([]);
    setAlertMessage('');
  };

  const handleSendAlert = async () => {
    if (selectedInventory.length === 0) {
      toast.error('Select at least one inventory item');
      return;
    }
    setSendingAlert(true);
    try {
      const payload = {
        inventory_ids: selectedInventory,
        message: alertMessage,
      };
      if (selectAll) {
        payload.send_to_all = true;
      } else {
        payload.prospect_ids = selected;
      }
      const r = await prospectsAPI.sendAlert(payload);
      toast.success(`Alert sent! SMS: ${r.data.sms_sent} sent, ${r.data.sms_failed} failed. Email: ${r.data.email_sent} sent, ${r.data.email_failed} failed.`);
      setAlertOpen(false);
      handleClearSelection();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send alerts');
    }
    setSendingAlert(false);
  };

  const toggleInventory = (id) => setSelectedInventory(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  );

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
      setUploadFile(file);
    } else {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
    }
  };

  return (
    <div data-testid="admin-prospects">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 uppercase" data-testid="prospects-title">Prospects</h1>
          <p className="text-sm text-gray-500 mt-0.5">{total} total prospect{total !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {(selected.length > 0 || selectAll) && (
            <>
              <Button variant="outline" size="sm" className="border-gray-300 text-xs h-8" onClick={openAlertDialog} data-testid="send-alert-btn">
                <Send className="w-3.5 h-3.5 mr-1.5" /> Send Alert ({selectAll ? `All ${total}` : selected.length})
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="border-gray-300 text-xs h-8" data-testid="bulk-alerts-toggle">
                    <Bell className="w-3.5 h-3.5 mr-1.5" /> Alerts
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-white">
                  <DropdownMenuItem onClick={() => handleBulkToggleAlerts(true)}>
                    <Bell className="w-3.5 h-3.5 mr-2 text-green-500" /> Enable Alerts
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleBulkToggleAlerts(false)}>
                    <BellOff className="w-3.5 h-3.5 mr-2 text-gray-400" /> Disable Alerts
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
          {total > 0 && !selectAll && (
            <Button variant="outline" size="sm" className="border-blue-200 text-blue-600 hover:bg-blue-50 text-xs h-8" onClick={handleSelectAllProspects} data-testid="select-all-prospects-btn">
              <Users className="w-3.5 h-3.5 mr-1.5" /> Select All ({total})
            </Button>
          )}
          {selectAll && (
            <Button variant="outline" size="sm" className="border-gray-300 text-xs h-8" onClick={handleClearSelection} data-testid="clear-selection-btn">
              <X className="w-3.5 h-3.5 mr-1.5" /> Clear Selection
            </Button>
          )}
          {isAdmin && total > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="border-red-200 text-red-500 hover:bg-red-50 text-xs h-8" disabled={deleting} data-testid="delete-menu-btn">
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" /> {deleting ? 'Deleting...' : 'Delete'}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white w-72">
                <DropdownMenuItem onClick={handleDeleteAll} className="text-red-600" data-testid="delete-all-btn">
                  <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete All Prospects ({total})
                </DropdownMenuItem>
                {batches.length > 0 && (
                  <>
                    <DropdownMenuSeparator />
                    <p className="px-2 py-1.5 text-[10px] font-medium text-gray-400 uppercase">Delete by import batch</p>
                    {batches.map(b => (
                      <DropdownMenuItem key={b.batch_id} onClick={() => handleDeleteBatch(b)} className="text-red-500" data-testid={`delete-batch-${b.batch_id}`}>
                        <FileSpreadsheet className="w-3.5 h-3.5 mr-2 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <span className="block text-xs truncate">{b.file}</span>
                          <span className="block text-[10px] text-gray-400">{b.count} records &middot; {b.imported_at ? new Date(b.imported_at).toLocaleDateString() : ''}</span>
                        </div>
                      </DropdownMenuItem>
                    ))}
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <Button className="bg-[#E31937] text-white hover:bg-[#C4122C] text-xs h-8" onClick={() => setUploadOpen(true)} data-testid="upload-excel-btn">
            <Upload className="w-3.5 h-3.5 mr-1.5" /> Import Excel
          </Button>
          {total > 0 && (
            <Button variant="outline" size="sm" className="border-gray-300 text-xs h-8" onClick={handleExport} disabled={exporting} data-testid="export-excel-btn">
              {exporting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}
              {exporting ? 'Exporting...' : 'Export Excel'}
            </Button>
          )}
          <Button variant="outline" size="sm" className="border-green-200 text-green-700 hover:bg-green-50 text-xs h-8" onClick={openTestEmail} data-testid="test-email-btn">
            <MailCheck className="w-3.5 h-3.5 mr-1.5" /> Test Email
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search by name, email, phone, city, make or model preference..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-9 h-9 text-sm"
            data-testid="prospects-search-input"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Select All Banner */}
      {selectAll && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2.5 mb-4 flex items-center justify-between" data-testid="select-all-banner">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-800">All {total} prospects selected</span>
          </div>
          <button onClick={handleClearSelection} className="text-blue-600 hover:text-blue-800 text-xs font-medium">Clear</button>
        </div>
      )}
      {!selectAll && selected.length === prospects.length && prospects.length > 0 && total > prospects.length && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 mb-4 flex items-center justify-between" data-testid="select-all-pages-hint">
          <span className="text-sm text-gray-600">All {prospects.length} on this page selected.</span>
          <button onClick={handleSelectAllProspects} className="text-blue-600 hover:text-blue-800 text-sm font-medium" data-testid="select-all-pages-btn">
            Select all {total} prospects
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-gray-200 hover:bg-transparent">
                <TableHead className="w-10">
                  <BulkSelectCheckbox
                    checked={selectAll || (selected.length === prospects.length && prospects.length > 0)}
                    onCheckedChange={() => {
                      if (selectAll || selected.length === prospects.length) {
                        handleClearSelection();
                      } else {
                        setSelected(prospects.map(pr => pr.id));
                      }
                    }}
                    testId="select-all-prospects"
                  />
                </TableHead>
                <TableHead className="text-gray-500 text-xs">Name</TableHead>
                <TableHead className="text-gray-500 text-xs hidden sm:table-cell">Phone</TableHead>
                <TableHead className="text-gray-500 text-xs hidden md:table-cell">Email</TableHead>
                <TableHead className="text-gray-500 text-xs hidden lg:table-cell">City</TableHead>
                <TableHead className="text-gray-500 text-xs hidden lg:table-cell">Preferences</TableHead>
                <TableHead className="text-gray-500 text-xs text-center">Alerts</TableHead>
                <TableHead className="text-gray-500 text-xs hidden md:table-cell">Last Alert</TableHead>
                <TableHead className="text-gray-500 text-xs text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} className="text-center text-gray-400 py-8">Loading...</TableCell></TableRow>
              ) : prospects.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-12">
                    <FileSpreadsheet className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 font-medium">No prospects found</p>
                    <p className="text-gray-400 text-sm mt-1">Upload an Excel file to import your client database</p>
                    <Button variant="outline" size="sm" className="mt-4 border-gray-300" onClick={() => setUploadOpen(true)} data-testid="empty-upload-btn">
                      <Upload className="w-3.5 h-3.5 mr-1.5" /> Import Excel
                    </Button>
                  </TableCell>
                </TableRow>
              ) : prospects.map(p => (
                <TableRow key={p.id} className="border-gray-200 text-gray-700">
                  <TableCell>
                    <BulkSelectCheckbox checked={selectAll || selected.includes(p.id)} onCheckedChange={() => toggleSelect(p.id)} testId={`select-prospect-${p.id}`} />
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium text-sm" data-testid={`prospect-name-${p.id}`}>{p.name || `${p.first_name} ${p.last_name}`}</p>
                      <p className="text-xs text-gray-400 sm:hidden">{p.phone || p.cell_phone || '--'}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-xs hidden sm:table-cell">{p.phone || p.cell_phone || '--'}</TableCell>
                  <TableCell className="text-xs hidden md:table-cell">{p.email || '--'}</TableCell>
                  <TableCell className="text-xs hidden lg:table-cell">{p.city || '--'}</TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <div className="flex flex-wrap gap-1">
                      {p.make_preference && <Badge variant="outline" className="text-[10px] border-gray-300 text-gray-600">{p.make_preference}</Badge>}
                      {p.model_preference && <Badge variant="outline" className="text-[10px] border-blue-200 text-blue-600">{p.model_preference}</Badge>}
                      {!p.make_preference && !p.model_preference && <span className="text-xs text-gray-400">--</span>}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <Switch
                      checked={p.alerts_enabled}
                      onCheckedChange={() => toggleAlerts(p)}
                      className="data-[state=checked]:bg-green-500"
                      data-testid={`toggle-alerts-${p.id}`}
                    />
                  </TableCell>
                  <TableCell className="text-xs text-gray-400 hidden md:table-cell">
                    {p.last_alert_at ? new Date(p.last_alert_at).toLocaleDateString() : 'Never'}
                  </TableCell>
                  <TableCell className="text-right">
                    {/* Desktop */}
                    <div className="hidden sm:flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-blue-500" title="Send Alert"
                        onClick={() => { setSelected([p.id]); openAlertDialog(); }}
                        data-testid={`alert-prospect-${p.id}`}
                      >
                        <Send className="w-3 h-3" />
                      </Button>
                      {isAdmin && (
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400" title="Delete"
                          onClick={() => handleDelete(p.id)}
                          data-testid={`delete-prospect-${p.id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                    {/* Mobile */}
                    <div className="sm:hidden">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`actions-menu-prospect-${p.id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-white">
                          <DropdownMenuItem onClick={() => { setSelected([p.id]); setAlertOpen(true); setSelectedInventory([]); setAlertMessage(''); }}>
                            <Send className="w-3.5 h-3.5 mr-2 text-blue-500" /> Send Alert
                          </DropdownMenuItem>
                          {isAdmin && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleDelete(p.id)} className="text-red-500">
                                <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <p className="text-xs text-gray-500">Page {page} of {totalPages}</p>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page <= 1}
                onClick={() => setPage(p => Math.max(1, p - 1))} data-testid="prev-page">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)} data-testid="next-page">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-900 uppercase">Import Prospects</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                dragOver ? 'border-[#E31937] bg-red-50' : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              onClick={() => document.getElementById('excel-file-input').click()}
              data-testid="upload-dropzone"
            >
              <FileSpreadsheet className="w-10 h-10 text-gray-400 mx-auto mb-3" />
              {uploadFile ? (
                <div>
                  <p className="font-medium text-sm text-gray-900">{uploadFile.name}</p>
                  <p className="text-xs text-gray-500 mt-1">{(uploadFile.size / 1024).toFixed(1)} KB</p>
                </div>
              ) : (
                <div>
                  <p className="font-medium text-sm text-gray-600">Drop your Excel file here</p>
                  <p className="text-xs text-gray-400 mt-1">or click to browse (.xlsx, .xls)</p>
                </div>
              )}
              <input
                id="excel-file-input"
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={(e) => { if (e.target.files[0]) setUploadFile(e.target.files[0]); }}
                data-testid="excel-file-input"
              />
            </div>

            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs font-medium text-gray-700 mb-1.5">Expected columns:</p>
              <div className="flex flex-wrap gap-1">
                {['Applicant', 'Firstname', 'Lastname', 'Address', 'City', 'Province', 'Postal',
                  'Homephone', 'Cellphone', 'Email ID 1', 'Email ID 2', 'Make Preference', 'Model Preference'
                ].map(c => (
                  <Badge key={c} variant="outline" className="text-[10px] border-gray-300 text-gray-500">{c}</Badge>
                ))}
              </div>
            </div>

            <div className="flex items-start gap-2 text-xs text-amber-600 bg-amber-50 rounded-lg p-3">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <p>Duplicates are automatically skipped based on matching phone numbers or email addresses.</p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="border-gray-300" onClick={() => { setUploadOpen(false); setUploadFile(null); }}>Cancel</Button>
            <Button className="bg-[#E31937] text-white hover:bg-[#C4122C]" disabled={!uploadFile || uploading} onClick={handleUpload} data-testid="confirm-upload-btn">
              {uploading ? 'Importing...' : 'Import'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Alert Dialog */}
      <Dialog open={alertOpen} onOpenChange={setAlertOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-900 uppercase">Send Inventory Alert</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500">
            Sending to <strong>{selectAll ? `ALL ${total}` : activeCount}</strong> prospect{activeCount !== 1 ? 's' : ''} via SMS &amp; Email
          </p>

          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-gray-700 mb-2 block">Select inventory items to include:</label>
              <div className="space-y-2 max-h-[300px] overflow-y-auto border border-gray-200 rounded-lg p-2">
                {inventory.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-4">No available inventory</p>
                ) : inventory.map(item => (
                  <div
                    key={item.id}
                    className={`flex items-center gap-3 p-2.5 border rounded-lg cursor-pointer transition-colors ${
                      selectedInventory.includes(item.id) ? 'border-[#E31937] bg-red-50' : 'border-gray-200 hover:bg-gray-50'
                    }`}
                    onClick={() => toggleInventory(item.id)}
                    data-testid={`alert-inventory-${item.id}`}
                  >
                    <Checkbox checked={selectedInventory.includes(item.id)} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{item.title || `${item.year} ${item.make} ${item.model}`}</p>
                      <p className="text-xs text-gray-500">
                        {item.price && item.price_public ? `$${item.price.toLocaleString()}` : 'Call for pricing'}
                        {item.stock_number ? ` | Stock #${item.stock_number}` : ''}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-gray-700 mb-1.5 block">Custom message (optional):</label>
              <textarea
                className="w-full border border-gray-200 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E31937]/20 focus:border-[#E31937]"
                rows={3}
                placeholder="Leave blank for auto-generated message with inventory details..."
                value={alertMessage}
                onChange={e => setAlertMessage(e.target.value)}
                data-testid="alert-message-input"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" className="border-gray-300" onClick={() => setAlertOpen(false)}>Cancel</Button>
            <Button
              className="bg-[#E31937] text-white hover:bg-[#C4122C]"
              disabled={sendingAlert || selectedInventory.length === 0}
              onClick={handleSendAlert}
              data-testid="confirm-send-alert-btn"
            >
              {sendingAlert ? 'Sending...' : `Send to ${selectAll ? `All ${total}` : activeCount} Prospect${activeCount !== 1 ? 's' : ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Action Bar */}
      <BulkActionBar
        count={selectAll ? total : selected.length}
        onDelete={handleBulkDelete}
        onClear={handleClearSelection}
        label="prospect(s)"
      />

      {/* Test Email Dialog */}
      <Dialog open={testEmailOpen} onOpenChange={setTestEmailOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-gray-900 uppercase">Test Email Delivery</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-gray-700 mb-1.5 block">Send test email to:</label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={testEmailAddr}
                  onChange={e => setTestEmailAddr(e.target.value)}
                  className="h-9 text-sm flex-1"
                  data-testid="test-email-input"
                />
                <Button
                  className="bg-[#E31937] text-white hover:bg-[#C4122C] text-xs h-9"
                  disabled={testEmailSending || !testEmailAddr}
                  onClick={handleTestEmail}
                  data-testid="send-test-email-btn"
                >
                  {testEmailSending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Send Test'}
                </Button>
              </div>
            </div>

            {testEmailResult && (
              <div className={`rounded-lg p-3 text-sm ${testEmailResult.success ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`} data-testid="test-email-result">
                <p className="font-medium">{testEmailResult.success ? 'Accepted by SendGrid (Status 202)' : 'Send Failed'}</p>
                {testEmailResult.error && <p className="text-xs mt-1">{testEmailResult.error}</p>}
                {testEmailResult.success && (
                  <p className="text-xs mt-1 text-green-600">Check your inbox and spam folder. If not received, your domain may need DNS authentication in SendGrid.</p>
                )}
              </div>
            )}

            {emailActivity.length > 0 && (
              <div>
                <p className="text-xs font-medium text-gray-700 mb-2">Recent Email Activity (from SendGrid):</p>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50 text-gray-500">
                        <th className="px-3 py-2 text-left">To</th>
                        <th className="px-3 py-2 text-left">Status</th>
                        <th className="px-3 py-2 text-left hidden sm:table-cell">Subject</th>
                      </tr>
                    </thead>
                    <tbody>
                      {emailActivity.map((m, i) => (
                        <tr key={i} className="border-t border-gray-100">
                          <td className="px-3 py-2 truncate max-w-[140px]">{m.to}</td>
                          <td className="px-3 py-2">
                            <Badge variant="outline" className={`text-[10px] ${
                              m.status === 'delivered' ? 'border-green-300 text-green-700' :
                              m.status === 'not_delivered' ? 'border-red-300 text-red-600' :
                              m.status === 'processed' ? 'border-blue-300 text-blue-600' :
                              'border-gray-300 text-gray-500'
                            }`}>{m.status}</Badge>
                          </td>
                          <td className="px-3 py-2 truncate max-w-[180px] hidden sm:table-cell text-gray-400">{m.subject}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs text-amber-700"><strong>Status "not_delivered"?</strong> Your domain needs DNS authentication. Go to <a href="https://app.sendgrid.com/settings/sender_auth" target="_blank" rel="noreferrer" className="underline font-medium">SendGrid → Settings → Sender Authentication</a> and add the required DNS records (SPF/DKIM) for <strong>canadiantrucksales.ca</strong>.</p>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
